import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const TICK_MS = 1000;

/**
 * 15-min remaining warning modal — pixel-faithful clone of real DUO popup.
 */
export type TimeWarningModalProps = {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  totalMinutes: number;
};

export function TimeWarningModal({
  open,
  onClose,
  sessionId,
  totalMinutes,
}: TimeWarningModalProps) {
  const ref = useRef<HTMLDialogElement | null>(null);
  const remainingMs = useRemainingMs(open, sessionId, totalMinutes);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  function handleBackdropClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === ref.current) onClose();
  }

  function handleCancel(e: React.SyntheticEvent<HTMLDialogElement>) {
    e.preventDefault();
    onClose();
  }

  const safeRemaining = Math.max(0, remainingMs);
  const minutes = Math.floor(safeRemaining / 60_000);
  const seconds = Math.floor((safeRemaining % 60_000) / 1000);

  return (
    <dialog
      ref={ref}
      data-skin="duo"
      onClick={handleBackdropClick}
      onCancel={handleCancel}
      aria-labelledby="time-warning-title"
      aria-describedby="time-warning-body"
      className="m-auto w-[480px] max-w-[calc(100%-2rem)] rounded-lg border-0 bg-[var(--duo-card)] p-0 text-[var(--duo-text)] shadow-[0_20px_25px_-5px_rgba(0,0,0,0.10)] backdrop:bg-black/50"
    >
      <div className="px-8 py-6">
        <div className="flex items-center justify-between">
          <h2 id="time-warning-title" className="text-[18px] font-semibold">
            Waarschuwing
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sluit waarschuwing"
            className="inline-flex h-8 w-8 items-center justify-center rounded-sm text-[var(--duo-text)] transition-colors hover:bg-[var(--duo-grey-cell)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue)] focus-visible:outline-offset-2"
          >
            <X size={20} strokeWidth={1.8} aria-hidden="true" />
          </button>
        </div>
        <p
          id="time-warning-body"
          aria-live="polite"
          className="mt-3 text-[14px] leading-relaxed text-[var(--duo-text)]"
        >
          Je examentijd is bijna om. Nog {minutes}{" "}
          {minutes === 1 ? "minuut" : "minuten"} en {seconds}{" "}
          {seconds === 1 ? "seconde" : "seconden"} resterend.
        </p>
      </div>
    </dialog>
  );
}

function useRemainingMs(open: boolean, sessionId: string, totalMinutes: number) {
  const totalMs = totalMinutes * 60 * 1000;
  const [elapsedMs, setElapsedMs] = useState(() => readStoredElapsed(sessionId));
  useEffect(() => {
    if (!open) return;
    setElapsedMs(readStoredElapsed(sessionId));
    const handle = setInterval(() => {
      setElapsedMs(readStoredElapsed(sessionId));
    }, TICK_MS);
    return () => clearInterval(handle);
  }, [open, sessionId]);
  return totalMs - elapsedMs;
}

function readStoredElapsed(sessionId: string): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(`a2-overall-timer-${sessionId}`);
    if (!raw) return 0;
    const v = Number.parseInt(raw, 10);
    return Number.isFinite(v) && v >= 0 ? v : 0;
  } catch {
    return 0;
  }
}
